import Swiper from "swiper";
import { Navigation, Pagination, Autoplay } from "swiper/modules";

/**
 * Initialize banner slideshow with video support
 */
function initBannerSlider(selector, options = {}) {
  const container = document.querySelector(selector);
  if (!container) return;

  // Default options
  const defaults = {
    draggable: true,
    autoplay: true,
    speed: 4000,
    pause: true,
    aspectRatio: 16 / 9,
    loop: false
  };

  const settings = { ...defaults, ...options };

  // Configure Swiper
  const swiper = new Swiper(container, {
    modules: [Navigation, Pagination, Autoplay],
    direction: "horizontal",
    loop: settings.loop,
    slidesPerView: 1,
    spaceBetween: 0,
    grabCursor: settings.draggable,
    touchEventsTarget: settings.draggable ? "container" : false,
    simulateTouch: settings.draggable,

    // Navigation
    navigation: {
      nextEl: ".swiper-button-next",
      prevEl: ".swiper-button-prev"
    },

    // Pagination
    pagination: {
      el: ".swiper-pagination",
      clickable: true
    },

    // Autoplay
    autoplay: settings.autoplay
      ? {
          delay: settings.speed,
          disableOnInteraction: false,
          pauseOnMouseEnter: settings.pause
        }
      : false,

    // Event hooks
    on: {
      init: () => resizeVideos(container),
      slideChangeTransitionStart: () => controlVideos(container, "pause"),
      slideChangeTransitionEnd: () => controlVideos(container, "play")
    }
  });

  // Resize listener
  window.addEventListener("resize", () => resizeVideos(container));

  return swiper;
}

/**
 * Post message to video players (YouTube, Vimeo)
 */
function postMessageToPlayer(player, command) {
  if (!player?.contentWindow) return;
  player.contentWindow.postMessage(JSON.stringify(command), "*");
}

/**
 * Control videos during slide changes
 */
function controlVideos(container, action) {
  const currentSlide = container.querySelector(".swiper-slide-active");
  if (!currentSlide) return;

  const player = currentSlide.querySelector(".slide-media");
  if (!player) return;

  const slideType = player.getAttribute("data-type");
  const startTime = currentSlide.dataset.videoStart;

  const commands = {
    vimeo: {
      play: () => {
        if (startTime && startTime > 0 && !currentSlide.classList.contains("started")) {
          currentSlide.classList.add("started");
          postMessageToPlayer(player, {
            method: "setCurrentTime",
            value: startTime
          });
        }
        postMessageToPlayer(player, { method: "play", value: 1 });
      },
      pause: () => postMessageToPlayer(player, { method: "pause", value: 1 })
    },
    youtube: {
      play: () => {
        postMessageToPlayer(player, { event: "command", func: "mute" });
        postMessageToPlayer(player, { event: "command", func: "playVideo" });
      },
      pause: () => postMessageToPlayer(player, { event: "command", func: "pauseVideo" })
    },
    hosted: {
      play: () => {
        const video = currentSlide.querySelector("video");
        video?.play();
      },
      pause: () => {
        const video = currentSlide.querySelector("video");
        video?.pause();
      }
    }
  };

  commands[slideType]?.[action]?.();
}

/**
 * Resize embedded video players
 */
function resizeVideos(container) {
  const iframes = container.querySelectorAll(".embed-player");
  if (!iframes.length) return;

  const width = container.offsetWidth;
  const height = container.offsetHeight;
  const ratio = 16 / 9; // Default aspect ratio

  iframes.forEach((iframe) => {
    let playerWidth, playerHeight;

    if (width / ratio < height) {
      playerWidth = Math.ceil(height * ratio);
      Object.assign(iframe.style, {
        width: `${playerWidth}px`,
        height: `${height}px`,
        left: `${(width - playerWidth) / 2}px`,
        top: "0px"
      });
    } else {
      playerHeight = Math.ceil(width / ratio);
      Object.assign(iframe.style, {
        width: `${width}px`,
        height: `${playerHeight}px`,
        left: "0px",
        top: `${(height - playerHeight) / 2}px`
      });
    }
  });
}

export { initBannerSlider };
